'use client';
import React from 'react';
import { ChevronDownIcon } from '@mentimeter/ragnar-visuals';
import { useTranslations } from '@mentimeter/i18n';
import ActionCollapsable, {
  type Action,
} from 'src/ui/investors/ActionCollapsable';
import { ActionMenu } from 'src/ui/header/ActionMenu';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Collapsable } from '@mentimeter/ragnar-ui/collapsable';

const actions: Action[] = [
  {
    name: 'Trust center',
    href: '/trust',
  },
  {
    name: 'Legal',
    referenceId: 'Legal',
    subActions: [
      {
        name: 'DPA statement',
        href: '/dpa-statement',
      },
      {
        name: 'GDPR transparency statement',
        href: '/trust/privacy/gdpr-transparency-statement',
      },
      {
        name: 'Guide',
        href: '/trust/legal/guide',
      },
      {
        name: 'Payments',
        href: '/trust/legal/payments',
      },
      {
        name: 'Privacy for kids',
        href: '/trust/legal/privacy-principles-for-kids',
      },
      {
        name: 'Open source',
        href: '/trust/legal/open-source',
      },
    ],
  },
  {
    name: 'Terms & conditions',
    referenceId: 'terms & conditions',
    subActions: [
      {
        name: 'Terms',
        href: '/trust/legal/terms',
      },
      {
        name: 'Enterprise terms',
        href: '/trust/legal/enterprise-terms',
      },
    ],
  },
  {
    name: 'Policies',
    referenceId: 'policies',
    subActions: [
      {
        name: 'Acceptable use policy',
        href: '/trust/legal/acceptable-use-policy',
      },
      {
        name: 'Privacy policy',
        href: '/trust/legal/privacy-policy',
      },
      {
        name: 'Security policy',
        href: '/trust/legal/security-policy',
      },
      {
        name: 'Cookie policy',
        href: '/trust/legal/cookie-policy',
      },
      {
        name: 'Candidate privacy policy',
        href: '/trust/legal/candidate-privacy-policy',
      },
      {
        name: 'Third-Party code of conduct',
        href: '/trust/legal/third-party-code-of-conduct',
      },
    ],
  },
  {
    name: 'Processors',
    referenceId: 'processors',
    subActions: [
      {
        name: 'Processors',
        href: '/trust/legal/processors',
      },
      {
        name: 'Subprocessors',
        href: '/trust/legal/subprocessors',
      },
    ],
  },
];

export const Navigation = () => {
  const [showMenu, setShowMenu] = React.useState<boolean>(false);
  const t = useTranslations('common');

  return (
    <Box
      zIndex={4}
      width="100%"
      alignItems="center"
      justifyContent="center"
      px={['space0', null, 'space4']}
      bg="bg"
      borderBottomWidth="1px"
      borderStyle="solid"
      borderColor="borderWeak"
      as="nav"
    >
      <Box
        display={['none', null, 'inherit']}
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        as="ul"
      >
        <ActionMenu
          actions={actions}
          overviewLabel={t('page_links.overview')}
        />
      </Box>
      <Box
        display={['inherit', null, 'none']}
        width="100%"
        py="space2"
        borderBottomWidth="1px"
        borderStyle="solid"
        borderColor="borderWeak"
        alignItems="center"
      >
        <Clickable
          aria-label="Menu"
          onClick={() => setShowMenu(!showMenu)}
          aria-expanded={showMenu}
          flexDirection="row"
          alignItems="center"
          extend={() => ({
            '@media (hover: hover)': {
              ':hover': {
                opacity: 1,
              },
            },
            ':active': {
              opacity: 1,
            },
          })}
        >
          <Text fontWeight="semiBold" color={showMenu ? 'primary' : 'text'}>
            Legal
          </Text>
          <Box
            ml="space2"
            extend={({ theme }) => ({
              transition: `transform ${theme.durations[1]}s ease`,
              transform: showMenu ? 'rotate(180deg)' : 'rotate(0)',
            })}
          >
            <ChevronDownIcon color={showMenu ? 'primary' : 'text'} size={2} />
          </Box>
        </Clickable>
      </Box>
      <Collapsable show={showMenu} width="100%">
        <Box
          as="nav"
          width="100%"
          borderBottomWidth="1px"
          borderStyle="solid"
          borderColor="borderWeak"
          bg="bg"
          px="space4"
          pt="space4"
        >
          {actions.map((a) => (
            <ActionCollapsable
              action={a}
              actions={a.subActions}
              setShowMenu={setShowMenu}
              key={a.name}
            />
          ))}
        </Box>
      </Collapsable>
    </Box>
  );
};
